import React, { useEffect, useState } from 'react';
import { Alert, Button, Divider, Modal, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import apiService from '@/services/apiService';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from "react-i18next";
import i18n from '@/i18n/config';

/**
 * 最近 24 小時 HKTV 執行錯誤橫幅。
 *
 * 排程拉單 / 取貨日期同步 / ACK 狀態同步的失敗沒有即時彈窗（只有手動拉單
 * 進行中有人盯著頁面才會看到彈窗），這個橫幅在頁面載入時把它們補回來。
 * 資料來源：GET /schedules/HKTVmall/recent-errors（hktv_job_runs 的
 * account_errors/order_errors + hktv_issues 24 小時摘要）。
 *
 * 注意：ACK 同步失敗只會出現在 hktv_issues，不會產生失敗的執行記錄，
 * 所以只要 issuesSummary 有資料也要顯示橫幅，不能只看 runs。
 */

export type AccountPullError = {
  storeCode: string
  errorType: 'auth' | 'api' | 'rate_limit' | 'unknown'
  message: string
  ordersAttempted: number
  skippedOrders: number
  occurredAt: string
}

export type OrderPullError = {
  storeCode: string
  orderNumber: string
  message: string
  occurredAt: string
}

export type RecentErrorRun = {
  id: number
  runType: 'manual' | 'scheduled' | string
  status: string
  tags: string[]
  startedAt: string
  finishedAt: string | null
  error: string | null
  accountErrors: AccountPullError[]
  orderErrors: OrderPullError[]
}

export type IssueSummaryRow = { storeCode: string; errorCode: string; count: number; lastOccurredAt: string }

/**
 * A function rather than a static map: this module has no React component of
 * its own to hold a `useTranslation()` hook (it is called from both this
 * file's Modal content and HKTVmallJobAndStatus.tsx's pull-completion popup),
 * so it reads through the i18next singleton directly. Evaluating on every
 * call — instead of once at module load — is what keeps it in sync when the
 * user switches language.
 */
export const getAccountErrorTypeLabel = (
  errorType: AccountPullError['errorType']
): { label: string; hint: string } => {
  switch (errorType) {
    case 'auth':
      return { label: i18n.t('認證過期 (401)'), hint: i18n.t('請到帳戶設定重新連線此商店') };
    case 'rate_limit':
      return { label: i18n.t('API 限流 (429)'), hint: i18n.t('稍後再試；新代碼會自動重試但部分仍可能失敗') };
    case 'api':
      return { label: i18n.t('HKTV API 錯誤'), hint: i18n.t('可能是暫時性，下次排程會自動重試') };
    default:
      return { label: i18n.t('其他錯誤'), hint: i18n.t('請查看詳細訊息') };
  }
};

export const runTypeLabel = (run: RecentErrorRun): string => {
  if ((run.tags || []).includes('pickup-date-sync')) return i18n.t('取貨日期同步');
  return run.runType === 'manual' ? i18n.t('手動拉單') : i18n.t('排程拉單');
};

// 商店錯誤表格（拉單完成彈窗與「最近錯誤」彈窗共用）
export const renderAccountErrorsTable = (accountErrors: AccountPullError[]) => {
  if (accountErrors.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <Typography.Text strong>{i18n.t('商店錯誤')} ({accountErrors.length})</Typography.Text>
      <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6, border: '1px solid #f0f0f0', borderRadius: 4 }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
            <tr>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('商店')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('類型')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('建議')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('訊息')}</th>
            </tr>
          </thead>
          <tbody>
            {accountErrors.map((e, i) => {
              const meta = getAccountErrorTypeLabel(e.errorType);
              return (
                <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{e.storeCode}</td>
                  <td style={{ padding: '6px 8px' }}><Tag color={e.errorType === 'auth' ? 'red' : e.errorType === 'rate_limit' ? 'orange' : 'gold'}>{meta.label}</Tag></td>
                  <td style={{ padding: '6px 8px', color: '#666' }}>{meta.hint}</td>
                  <td style={{ padding: '6px 8px', color: '#999', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.message}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 訂單錯誤表格（同上，兩個彈窗共用）
export const renderOrderErrorsTable = (orderErrors: OrderPullError[]) => {
  if (orderErrors.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <Typography.Text strong>{i18n.t('訂單錯誤')} ({orderErrors.length})</Typography.Text>
      <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6, border: '1px solid #f0f0f0', borderRadius: 4 }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
            <tr>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('訂單號')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('商店')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{i18n.t('訊息')}</th>
            </tr>
          </thead>
          <tbody>
            {orderErrors.map((e, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{e.orderNumber}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{e.storeCode}</td>
                <td style={{ padding: '6px 8px', color: '#999', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * 可掛在任何頁面的橫幅本體。無資料或載入失敗時不佔空間。
 * @param withDivider 顯示時是否在上方加 Divider（Job 頁沿用原有排版）
 */
/**
 * Roles that operate the HKTV integration. Anyone else — merchants signing in
 * to the customer portal — must not see this: it names other tenants' store
 * codes and their credential failures, which is not their information and not
 * actionable by them. The endpoint is role-guarded server-side too; this stops
 * the request being made at all.
 */
const HKTV_OPERATOR_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MANAGER']);

const HktvRecentErrorsBanner: React.FC<{ withDivider?: boolean }> = ({ withDivider = false }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const canSeeIntegrationErrors = !!user?.role && HKTV_OPERATOR_ROLES.has(user.role);
  const [runs, setRuns] = useState<RecentErrorRun[]>([]);
  const [issuesSummary, setIssuesSummary] = useState<IssueSummaryRow[]>([]);
  const [dismissed, setDismissed] = useState<boolean>(false);

  useEffect(() => {
    if (!canSeeIntegrationErrors) return;
    (async () => {
      try {
        const res: any = await apiService.get('/schedules/HKTVmall/recent-errors');
        if (res?.success && res?.data) {
          setRuns(Array.isArray(res.data.runs) ? res.data.runs : []);
          setIssuesSummary(Array.isArray(res.data.issuesSummary) ? res.data.issuesSummary : []);
        }
      } catch {
        // 靜默失敗：橫幅是輔助功能，不應阻擋頁面
      }
    })();
  }, [canSeeIntegrationErrors]);

  const showDetailModal = () => {
    Modal.warning({
      title: t('最近 24 小時執行錯誤'),
      width: 760,
      content: (
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          {runs.map(run => (
            <div key={run.id} style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
              <Space>
                <Tag color={run.runType === 'manual' ? 'blue' : 'purple'}>{runTypeLabel(run)}</Tag>
                <Typography.Text strong>{dayjs(run.startedAt).format('MM-DD HH:mm')}</Typography.Text>
                {(run.tags || []).filter(t => t !== 'pickup-date-sync').map(t => <Tag key={t}>{t}</Tag>)}
                {run.status === 'FAILED' && <Tag color="red">{t('整體失敗')}</Tag>}
              </Space>
              {run.error && <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>{run.error}</div>}
              {renderAccountErrorsTable(run.accountErrors || [])}
              {renderOrderErrorsTable(run.orderErrors || [])}
            </div>
          ))}
        </div>
      ),
      okText: t('我知道了'),
    });
  };

  // Explicit, not merely a consequence of the skipped fetch: a future caller
  // passing data in must not be able to surface this to a customer.
  if (!canSeeIntegrationErrors) return null;
  if (dismissed || (runs.length === 0 && issuesSummary.length === 0)) return null;

  return (
    <>
      {withDivider && <Divider style={{ margin: '16px 0' }} />}
      <Alert
        type="warning"
        showIcon
        closable
        onClose={() => setDismissed(true)}
        style={withDivider ? undefined : { marginBottom: 12 }}
        message={runs.length > 0
          ? t('最近 24 小時有 {{count}} 次 HKTV 執行出現錯誤（最近一次：{{time}} {{type}}）', {
              count: runs.length,
              time: dayjs(runs[0].startedAt).format('MM-DD HH:mm'),
              type: runTypeLabel(runs[0]),
            })
          : t('最近 24 小時有 HKTV 401 認證錯誤（拉單執行本身正常）')}
        description={
          <Space direction="vertical" size={4}>
            {runs.length > 0 && (
              <span>
                {t('受影響商店：')}{Array.from(new Set(runs.flatMap(r => (r.accountErrors || []).map(e => e.storeCode)))).slice(0, 8).join('、') || '—'}
              </span>
            )}
            {issuesSummary.length > 0 && (
              <span>
                {t('401 認證錯誤（24h）：')}{issuesSummary.slice(0, 5).map(s => s.storeCode).join('、')} {t('— 詳見 HKTVmall Issues 頁')}
                <Typography.Text type="danger" style={{ fontSize: 12, display: 'block' }}>
                  {t('該商店的 API 憑證已失效，請到帳戶設定更新後重新測試連線')}
                </Typography.Text>
              </span>
            )}
            {runs.length > 0 && (
              <Button size="small" onClick={showDetailModal}>{t('查看詳情')}</Button>
            )}
          </Space>
        }
      />
    </>
  );
};

export default HktvRecentErrorsBanner;
